import React, { useContext, useState } from 'react';
import { FormikValues } from 'formik';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import * as Yup from 'yup';
import { Context } from '@components/Context';
import { NavContext } from '@components/navigation/Context';
import Colors from '@components/shared/Colors';
import FormContainer from '@components/shared/Form';
import { P } from '@components/shared/Typography';
import { FieldType, FormField, CartItemType, GiftCardCartItem, NavStatus, ProductCode } from '@contracts';
import TickIcon from '@images/tick.svg';
import { getUniqueCartId } from '@utils/cart';

const GiftCardForm = () => {
  const { t } = useTranslation();
  const { state, dispatch } = useContext(Context);
  const { navDispatch } = useContext(NavContext);
  const { currency } = state;
  const [price, setPrice] = useState(10);

  const initialValues = {
    senderName: '',
    recipientName: '',
    recipientEmail: '',
    message: ''
  };

  const fields: FormField[] = [
    {
      id: `senderName`,
      type: FieldType.Text,
      label: `Sender's name`,
      placeholder: `First name only`
    },
    {
      id: `recipientName`,
      type: FieldType.Text,
      label: `Recipient’s name`,
      placeholder: `First name only`
    },
    {
      id: `recipientEmail`,
      type: FieldType.Email,
      label: `Recipient’s email`,
      placeholder: `email@example.com`,
      description: `Enter your own email address if you don’t want the card delivered immediately.`
    },
    {
      id: `message`,
      type: FieldType.Textarea,
      label: `Personalised message (optional)`,
      placeholder: `Make the gift more personal!`
    }
  ];

  const ValidationSchema = Yup.object().shape({
    senderName: Yup.string().required('This field is required'),
    recipientName: Yup.string().required('This field is required'),
    recipientEmail: Yup.string().email('Email address is invalid').required('This field is required')
  });

  const onSubmit = (values: FormikValues) => {
    const details = {
      recipient: {
        name: values.recipientName,
        email: values.recipientEmail
      },
      sender: {
        name: values.senderName
      },
      message: values.message
    };
    dispatch({
      type: 'cartItemAdd',
      data: {
        id: getUniqueCartId(state, ProductCode.GiftCard),
        type: CartItemType.GiftCard,
        productCode: ProductCode.GiftCard,
        unique: true,
        price,
        currency,
        quantity: 1,
        details
      } as GiftCardCartItem
    });
    navDispatch({ type: 'setStatus', data: NavStatus.Basket });
  };

  const optionPrices = [10, 20, 30, 40];

  return (
    <Container id="gift-card-form">
      <Label>Gift card amount</Label>
      <PriceSelector>
        {optionPrices.map((optionPrice, index) => (
          <StyledButton key={index} onClick={() => setPrice(optionPrice)} $isSelected={price === optionPrice}>
            {price === optionPrice && <TickIcon style={{ width: '16px', marginRight: '8px' }} />}
            {currency.symbol}
            {optionPrice}
          </StyledButton>
        ))}
      </PriceSelector>
      <FormContainer
        initialValues={initialValues}
        fields={fields}
        validationSchema={ValidationSchema}
        onSubmit={(values: FormikValues) => onSubmit(values)}
        submitButtonText={t('components.add-to-cart')}
      />
    </Container>
  );
};

export default GiftCardForm;

interface StyleProps {
  $isSelected?: boolean;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  width: 580px;
  max-width: 100%;
  margin: auto;
  padding-top: 100px;

  @media (max-width: 768px) {
    padding: 80px 30px 0;
  }

  & form {
    height: auto;
    overflow: hidden;
    width: 100%;

    textarea {
      max-width: 100%;
    }

    & button[type='submit'] {
      float: right;
    }
  }
`;

const PriceSelector = styled.div`
  margin-bottom: 30px;
  margin-top: 15px;
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
`;

const StyledButton = styled.button<StyleProps>`
  all: unset;
  cursor: pointer;
  font-family: Domaine;
  font-size: 24px;
  font-weight: 500;
  line-height: 24px;
  text-align: center;

  @media (max-width: 768px) {
    font-size: 18px;
  }

  width: 130px;
  height: 74px;
  display: flex;
  justify-content: center;
  align-items: center;
  border: 1px solid ${Colors.paleGold};
  ${(props) => {
    if (props.isSelected) {
      return `border: 2px solid ${Colors.gold}; background: ${Colors.ecru}80`;
    }
  }}
`;

const Label = styled(P)`
  font-size: 15px;
  font-weight: 700;
  line-height: 24px;
  letter-spacing: 0;
  text-align: left;
  margin-top: 0;

  &:first-child {
    margin-top: 0;
  }
`;
